import React from "react";
import {
  X,
  FileText,
  Shield,
  CreditCard,
  Users,
  AlertTriangle,
  Scale,
  Globe,
} from "lucide-react";

interface TermsConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TermsConditionsModal: React.FC<TermsConditionsModalProps> = ({
  isOpen,
  onClose,
}) => {
     const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-300"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 p-6 text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-purple-700/20"></div>

          <button
            onClick={onClose}
            className="absolute top-1 right-5 p-2 !z-[9999] hover:bg-white/20 rounded-xl transition-all duration-200 hover:scale-110"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-2">Terms & Conditions</h2>
              <p className="text-indigo-100">
                QCBT Platform - Effective Date: January 1, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
          <div className="prose prose-slate max-w-none">
            {/* Introduction */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-8 border border-indigo-200">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-indigo-600" />
                <h3 className="text-xl font-bold text-indigo-900 m-0">
                  Agreement to Terms
                </h3>
              </div>
              <p className="text-indigo-800 m-0">
                By accessing and using QCBT Platform, you accept and agree to be
                bound by the terms and provision of this agreement. These Terms
                & Conditions govern your use of our computer-based testing
                platform and related services.
              </p>
            </div>

            {/* Service Description */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  Service Description
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">
                    Platform Features
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Computer-based testing platform</li>
                    <li>• AI-powered proctoring system</li>
                    <li>• Face verification technology</li>
                    <li>• Real-time monitoring</li>
                    <li>• Automated result processing</li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">
                    SaaS Model
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Credit-based subscription system</li>
                    <li>• Multi-tenant architecture</li>
                    <li>• Scalable infrastructure</li>
                    <li>• 24/7 technical support</li>
                    <li>• Regular platform updates</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Subscription & Billing */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  Subscription & Billing
                </h3>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                <h4 className="font-semibold text-green-800 mb-3">
                  Credit-Based System
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-green-800">
                        Credit Purchase
                      </p>
                      <p className="text-sm text-green-700">
                        Credits are purchased in advance and consumed based on
                        platform usage.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-green-800">
                        Usage Tracking
                      </p>
                      <p className="text-sm text-green-700">
                        Each exam session, proctoring minute, and verification
                        consumes credits.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-green-800">
                        Pay-as-You-Go
                      </p>
                      <p className="text-sm text-green-700">
                        No recurring charges - purchase credits only when needed
                        for your examinations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <h4 className="font-semibold text-amber-800 m-0">
                    Billing Terms
                  </h4>
                </div>
                <p className="text-amber-700 text-sm m-0">
                  All fees are non-refundable except as required by law. Payment
                  disputes must be raised within 30 days of billing.
                </p>
              </div>
            </div>

            {/* User Responsibilities */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  User Responsibilities
                </h3>
              </div>

              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-slate-800">
                    Account Security
                  </h4>
                  <p className="text-slate-600 text-sm">
                    You are responsible for maintaining the confidentiality of
                    your account credentials and for all activities that occur
                    under your account.
                  </p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-slate-800">
                    Prohibited Activities
                  </h4>
                  <p className="text-slate-600 text-sm">
                    You may not attempt to circumvent security measures, share
                    exam content, or engage in any form of academic dishonesty.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-slate-800">Compliance</h4>
                  <p className="text-slate-600 text-sm">
                    You must comply with all applicable laws, regulations, and
                    institutional policies when using our platform.
                  </p>
                </div>
              </div>
            </div>

            {/* AI Proctoring Terms */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  AI Proctoring & Monitoring
                </h3>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Consent to Monitoring
                    </h4>
                    <p className="text-blue-700 text-sm">
                      By using our platform, you consent to video and audio
                      recording, facial recognition, and behavioral analysis
                      during exam sessions.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Data Processing
                    </h4>
                    <p className="text-blue-700 text-sm">
                      AI algorithms analyze your behavior to detect potential
                      violations of exam integrity. This data is processed
                      securely and used solely for examination purposes.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">
                      Review Process
                    </h4>
                    <p className="text-blue-700 text-sm">
                      Flagged sessions may be reviewed by human proctors. You
                      have the right to appeal any integrity violations through
                      your institution.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Limitation of Liability
              </h3>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <h4 className="font-semibold text-red-800 m-0">
                    Important Disclaimer
                  </h4>
                </div>
                <div className="space-y-3 text-sm text-red-700">
                  <p>
                    QCBT Platform provides services "as is" without warranties
                    of any kind. We are not liable for any indirect, incidental,
                    special, or consequential damages.
                  </p>
                  <p>
                    Our total liability shall not exceed the amount paid by you
                    for the services in the 12 months preceding the claim.
                  </p>
                  <p>
                    We do not guarantee uninterrupted service and are not
                    responsible for technical issues beyond our reasonable
                    control.
                  </p>
                </div>
              </div>
            </div>

            {/* Termination */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-800 mb-4">
                Termination
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">By You</h4>
                  <p className="text-sm text-slate-600">
                    You may terminate your account at any time through your
                    account settings. Unused credits will be forfeited upon
                    termination.
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-2">By Us</h4>
                  <p className="text-sm text-slate-600">
                    We may terminate accounts for violations of these terms,
                    non-payment, or other legitimate business reasons with
                    appropriate notice.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-6 h-6 text-slate-600" />
                <h3 className="text-xl font-bold text-slate-800 m-0">
                  Contact Information
                </h3>
              </div>
              <p className="text-slate-700 mb-4">
                For questions about these Terms & Conditions or our services,
                please contact us:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-medium text-slate-800">Customer Support</p>
                  <p className="text-slate-600">cbt@msqube.com</p>
                </div>
                <div className="col-span-2">
                  <p className="font-medium  text-slate-800">
                    Business Address
                  </p>
                  <p className="text-slate-600">
                    Unit #112, SDF Building Near RDB Cinemas, GP Block, Sector
                    V, Bidhannagar Kolkata, West Bengal 700091
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
